import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { useParams } from "react-router-dom";

import { Alert } from "components/Alert";
import { Badge } from "components/Badge";
import { Button } from "components/Button";
import { PageWithNav } from "components/PageWithNav";
import { Spinner } from "components/Spinner";
import { Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "components/Table";
import api from "utils/api";

export const ViewStudent = () => {
  const { studentId } = useParams();

  // state
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({});
  const [hasError, setHasError] = useState(false);

  const loadStudentData = useCallback(async () => {
    try {
      const response = await api.get(`/student/${studentId}`);
      setData(response.data.user);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [studentId]);

  const handleCerificateSent = async (courseId, userTestId) => {
    await api.put(`/course/${courseId}/${userTestId}/send-certificate`, {
      certificate_sent: true,
    });
    loadStudentData();
  };

  const getCertificateStatus = (has_passed, certificate_sent) => {
    if (has_passed && certificate_sent) {
      return <Badge type="success">Certificate Sent</Badge>;
    }
    if (has_passed && !certificate_sent) {
      return <Badge type="danger">Awaiting Certificate</Badge>;
    }

    return <Badge>Not Applicable</Badge>;
  };

  useEffect(() => {
    loadStudentData();
  }, [loadStudentData]);

  if (isLoading) {
    return (
      <PageWithNav>
        <div className="flex justify-center">
          <Spinner size="lg" color="primary" />
        </div>
      </PageWithNav>
    );
  }

  if (hasError) {
    return (
      <PageWithNav>
        <div className="flex justify-center text-xl font-semibold leading-3 text-red-900">There was a problem, refresh the page to try again.</div>
      </PageWithNav>
    );
  }

  return (
    <PageWithNav>
      <div className="flex flex-col gap-4">
        <div className="flex w-full flex-shrink-0 flex-col gap-2 rounded-md border bg-white p-4">
          <p className="text-sm text-gray-500">
            System ID: <span className="font-semibold text-gray-800">{data.id}</span>
          </p>
          <div className="flex flex-col space-y-1 sm:flex-row sm:items-center sm:space-x-4 sm:space-y-0">
            <h1 className="text-2xl font-semibold text-gray-800">{data.name}</h1>
          </div>
          <p className="text-md text-gray-500">
            {data.email} - {data.phone_number}
          </p>
          <p className="text-sm text-gray-900">Registered: {format(data.created_at, "do MMM yyyy")}</p>
          <div>
            <Badge type={data.email_verified_at ? "success" : "danger"}>{data.email_verified_at ? "Email verified" : "Email not verified"}</Badge>
          </div>
        </div>

        {/* show purchased courses */}
        <div className="flex w-full flex-shrink-0 flex-col gap-2 rounded-md border bg-white p-4">
          {!data?.purchases?.length && <p className="text-md text-center text-neutral-500">User does not have any products.</p>}
          {!!data?.purchases?.length && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Product</TableHeaderCell>
                  <TableHeaderCell>Stripe Payment ID</TableHeaderCell>
                  <TableHeaderCell>Payment date</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.purchases.map((purchase, index) => (
                  <TableRow key={index.toString()}>
                    <TableCell>{purchase.name}</TableCell>
                    <TableCell>{purchase.pivot.stripe_payment_intent_id}</TableCell>
                    <TableCell>{format(purchase.pivot.created_at, "do 'of' MMMM yyyy HH:mm")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* show refunds */}
        <div className="flex w-full flex-shrink-0 flex-col gap-2 rounded-md border bg-white p-4">
          {!data?.refunds?.length && <p className="text-md text-center text-neutral-500">User has not refunded any products.</p>}
          {!!data?.refunds?.length && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Product</TableHeaderCell>
                  <TableHeaderCell>Refund date</TableHeaderCell>
                  <TableHeaderCell>Refund receipt url</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.refunds.map((refund, index) => (
                  <TableRow key={index.toString()}>
                    <TableCell>{refund.name}</TableCell>
                    <TableCell>{format(refund.pivot.created_at, "do 'of' MMMM yyyy HH:mm")}</TableCell>
                    <TableCell>
                      <a className="font-medium text-blue-600 hover:underline" href={refund.pivot.receipt_url} target="_blank" rel="noopener noreferrer">
                        View Refund Receipt
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* show completed course (user) tests */}
        <div className="flex w-full flex-shrink-0 flex-col gap-2 rounded-md border bg-white p-4">
          {!data?.course_tests?.length && <p className="text-md text-center text-neutral-500">User has not completed any course tests.</p>}
          {!!data?.course_tests?.length && (
            <>
              {data.needs_certification && (
                <Alert type="warning">
                  <span>Student has completed one or more courses and has not recieved their ceritification.</span>
                </Alert>
              )}
              {data.course_tests
                .filter((courseTest) => !courseTest.certificate_sent && courseTest.has_passed)
                .map((courseTest, i) => {
                  return (
                    <Alert key={i.toString()}>
                      <div className="flex w-full flex-wrap items-center justify-between">
                        <div>
                          Course: <strong>{courseTest?.course?.name}</strong> - Have you sent certificate?
                        </div>
                        <Button size="sm" color="success" onClick={() => handleCerificateSent(courseTest?.course?.id, courseTest.id)}>
                          Yes
                        </Button>
                      </div>
                    </Alert>
                  );
                })}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>Course</TableHeaderCell>
                    <TableHeaderCell>Result</TableHeaderCell>
                    <TableHeaderCell>Score</TableHeaderCell>
                    <TableHeaderCell>Certificate Status</TableHeaderCell>
                    <TableHeaderCell>Completed at</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.course_tests.map((courseTest, index) => (
                    <TableRow key={index.toString()}>
                      <TableCell>{courseTest?.course?.name}</TableCell>
                      <TableCell>
                        <Badge type={courseTest.has_passed ? "success" : "danger"}>{courseTest.has_passed ? "Passed" : "Failed"}</Badge>
                      </TableCell>
                      <TableCell>{courseTest.overall_score}%</TableCell>
                      <TableCell>{getCertificateStatus(courseTest.has_passed, courseTest.certificate_sent)}</TableCell>
                      <TableCell className="w-0">{format(courseTest.created_at, "do 'of' MMMM yyyy HH:mm")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </div>
      </div>
    </PageWithNav>
  );
};
